import { useNavigate } from 'react-router-dom'
import { useQuietMode } from '../components/QuietModeProvider'
import { useTheme } from '../components/ThemeProvider'

const About = () => {
  const navigate = useNavigate()
  const { isQuietMode } = useQuietMode()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-sage-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-white/60 dark:bg-sage-800/60 border-b border-sage-100/30 dark:border-sage-700/30">
        <button
          onClick={() => navigate('/home')}
          className="p-3 rounded-2xl hover:bg-sage-100/60 dark:hover:bg-sage-700/60 transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-700/50"
        >
          <svg className="w-5 h-5 text-sage-500 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-light text-sage-600 dark:text-sage-300">About Mind Polish</h1>
        <div className="w-11"></div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* App Description */}
        <section className="mb-8 sm:mb-12">
          <div className="bg-white/80 dark:bg-sage-800/80 rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-6 sm:p-8 shadow-sm">
            <div className="text-center mb-6 sm:mb-8">
              <div className="w-16 h-16 mx-auto mb-6 bg-sage-100/60 dark:bg-sage-700/60 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-sage-400 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h2 className="text-sage-600 dark:text-sage-300 font-light text-lg sm:text-xl mb-4">What is Mind Polish?</h2>
            </div>
            
            <p className="text-sage-600 dark:text-sage-300 font-light text-base sm:text-lg leading-relaxed text-center mb-6">
              Mind Polish is a calm, adaptive mental wellness companion designed especially for neurodivergent and sensitive minds.
            </p>
            
            {!isQuietMode && (
              <p className="text-sage-500 dark:text-sage-400 font-light leading-relaxed text-center">
                We believe that mental wellness isn't one-size-fits-all. Our gentle tools adapt to your energy levels, 
                sensory needs, and emotional state, creating a truly personalized sanctuary for your wellbeing.
              </p>
            )}
          </div>
        </section>

        {/* Philosophy */}
        <section className="mb-8 sm:mb-12">
          <div className="bg-mint-50/60 dark:bg-sage-800/60 rounded-3xl border border-mint-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <h3 className="text-mint-600 dark:text-mint-300 font-light text-base sm:text-lg mb-6 text-center">Our Philosophy</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-2 h-2 bg-mint-400 dark:bg-mint-500 rounded-full mt-3 flex-shrink-0"></div>
                <div>
                  <h4 className="text-mint-600 dark:text-mint-300 font-light text-sm sm:text-base mb-2">Emotional Safety First</h4>
                  <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                    Every interaction is designed to feel safe, non-judgmental, and supportive of wherever you are today.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-2 h-2 bg-mint-400 dark:bg-mint-500 rounded-full mt-3 flex-shrink-0"></div>
                <div>
                  <h4 className="text-mint-600 dark:text-mint-300 font-light text-sm sm:text-base mb-2">No Labels, Just Support</h4>
                  <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                    We don't diagnose or categorize. We simply offer tools and companionship for your unique journey.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-2 h-2 bg-mint-400 dark:bg-mint-500 rounded-full mt-3 flex-shrink-0"></div>
                <div>
                  <h4 className="text-mint-600 dark:text-mint-300 font-light text-sm sm:text-base mb-2">Responsive Support</h4>
                  <p className="text-mint-500 dark:text-mint-400 font-light text-xs sm:text-sm leading-relaxed">
                    Our tools adapt to your energy levels, offering gentle alternatives when you need them most.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Meet Dopa */}
        <section className="mb-8 sm:mb-12">
          <div className="bg-peach-50/60 dark:bg-sage-800/60 rounded-3xl border border-peach-100/50 dark:border-sage-700/50 p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 mx-auto mb-4 bg-peach-100/60 dark:bg-peach-900/30 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-peach-400 dark:text-peach-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-peach-600 dark:text-peach-300 font-light text-base sm:text-lg mb-4">Meet Dopa</h3>
            </div>
            
            <p className="text-peach-600 dark:text-peach-300 font-light leading-relaxed text-center mb-4">
              Dopa is our gentle AI companion that grows with you.
            </p>
            
            {!isQuietMode && (
              <p className="text-peach-500 dark:text-peach-400 font-light text-xs sm:text-sm leading-relaxed text-center">
                Unlike traditional chatbots, Dopa learns your patterns, preferences, and needs to offer 
                increasingly personalized support. Think of Dopa as a caring friend who remembers what 
                helps you feel better and gently suggests it when you need it most.
              </p>
            )}
          </div>
        </section>

        {/* Built With Care */}
        <section>
          <div className="bg-cream-50/60 dark:bg-sage-800/60 rounded-3xl border border-cream-100/50 dark:border-sage-700/50 p-6 sm:p-8 text-center">
            <h3 className="text-cream-600 dark:text-cream-300 font-light text-base sm:text-lg mb-4">Built with Care</h3>
            <p className="text-cream-500 dark:text-cream-400 font-light leading-relaxed">
              Mind Polish is crafted by a team who understands that mental wellness is deeply personal. 
              Every feature is designed with empathy, accessibility, and genuine care for your wellbeing.
            </p>
            
            {!isQuietMode && (
              <div className="mt-6 pt-6 border-t border-cream-200/30 dark:border-sage-700/30">
                <p className="text-cream-400 dark:text-cream-500 font-light text-xs sm:text-sm">
                  Questions or feedback? We'd love to hear from you.
                </p>
                <button
                  onClick={() => navigate('/contact')}
                  className="mt-3 text-cream-500 dark:text-cream-400 text-xs sm:text-sm underline hover:text-cream-600 dark:hover:text-cream-300 transition-colors"
                >
                  Get in touch
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default About